import { logWarn } from 'lib/logging'

/** A template as the interim browser stores saved it, before templates lived on the workspace. */
export type BrowserTemplate = { workspaceId: string; fallbacks?: Record<string, string> }

type ImportOptions<Local extends BrowserTemplate, Input> = {
  storageKey: string
  workspaceId: string
  toInput: (template: Local) => Input
  /** The workspace's templates as the server holds them now. */
  fetchServer: () => Promise<{ rows: Input[]; failed: boolean }>
  create: (input: Input) => Promise<{ error?: unknown }>
  sameTemplate: (a: Input, b: Input) => boolean
}

function readBrowserTemplates<Local>(storageKey: string): Local[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) ?? `[]`) as unknown
    return Array.isArray(parsed) ? (parsed as Local[]) : []
  } catch (err) {
    logWarn(`browserTemplateImport(${storageKey}): unreadable, skipping`, err)
    return []
  }
}

function writeBrowserTemplates(storageKey: string, templates: unknown[]) {
  try {
    if (templates.length) localStorage.setItem(storageKey, JSON.stringify(templates))
    else localStorage.removeItem(storageKey)
  } catch (err) {
    logWarn(`browserTemplateImport(${storageKey}): write failed`, err)
  }
}

async function importUnlocked<Local extends BrowserTemplate, Input>(opts: ImportOptions<Local, Input>) {
  const stored = readBrowserTemplates<Local>(opts.storageKey)
  const mine = stored.filter((t) => t.workspaceId === opts.workspaceId)
  if (!mine.length) return 0

  const server = await opts.fetchServer()
  if (server.failed) return 0

  const known = [...server.rows]
  const kept: Local[] = []
  let imported = 0
  for (const template of mine) {
    const input = opts.toInput(template)
    if (known.some((row) => opts.sameTemplate(row, input))) continue
    const result = await opts.create(input)
    if (result.error) {
      logWarn(`browserTemplateImport(${opts.storageKey}): create failed, keeping the browser copy`, result.error)
      kept.push(template)
      continue
    }
    known.push(input)
    imported++
  }

  // Re-read so a template another tab saved meanwhile is not lost.
  const others = readBrowserTemplates<Local>(opts.storageKey).filter((t) => t.workspaceId !== opts.workspaceId)
  writeBrowserTemplates(opts.storageKey, [...others, ...kept])
  return imported
}

/**
 * Move this browser's saved templates for the open workspace onto the server, once. A template
 * the server already holds is dropped rather than copied, and a browser copy is removed only after
 * its create succeeded. Other workspaces' templates stay until that workspace is opened. Returns
 * how many templates were created.
 */
export async function importBrowserTemplates<Local extends BrowserTemplate, Input>(
  opts: ImportOptions<Local, Input>,
): Promise<number> {
  if (typeof localStorage === `undefined` || !readBrowserTemplates(opts.storageKey).length) return 0
  // Two tabs opening the workspace at once would each import the same templates.
  const locks = typeof navigator === `undefined` ? undefined : navigator.locks
  if (!locks) return importUnlocked(opts)
  return locks.request(`trk:import:${opts.storageKey}`, () => importUnlocked(opts))
}
